/**
 * accountMemberGroups.component.tsx
 * Sentriqs
 *
 * Created by: Dylan Knight
 * Copyright (c) 2024 Sentriqs, Inc. All rights reserved.
 *
**/

import React from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionSummary, AccordionDetails, Button } from "@mui/material"

import { MemberTypeDropdown } from "../components/memberTypeDropdown.component";

import { AuthContext } from "../auth/auth.context";

import { Device, Membership } from "../models/auth";
import { DeviceMembership, GroupMembership, GroupType,
  GroupMemberType } from "../models/group";

import { groupServiceFactory } from "../services/group.service";

interface Props {
  member: Membership,
  devices: Array<Device>,
  onError: (err: Error) => void
}
export function AccountMemberGroupsComponent(props: Props): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const [groupMemberships, setGroupMembers] = React.useState<Array<GroupMembership>>([]);
  const [directMessages, setDMs] = React.useState<Array<GroupMembership>>([]);
  const [meetings, setMeetings] = React.useState<Array<GroupMembership>>([]);
  const [devMap, setDevMap] = React.useState<{[id: string]: Device}>({});
  const [memberTypeMenuAnchorEl, setMemberTypeMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [currentMemberType, setCurrentMemberType] = React.useState<GroupMemberType | null>(null);
  const [menuGroupMember, setMenuGroupMember] = React.useState<GroupMembership | null>(null);

  React.useEffect(function () {
    const devMap: {[id: string]: Device} = {};
    for (const device of props.devices) {
      devMap[device._id] = device;
    }
    setDevMap(devMap);
  }, [props.devices]);

  React.useEffect(function () {
    groupServiceFactory().getGroupMembersForUser(props.member.accountID, props.member.userID)
    .then(processMemberships, props.onError);
  }, []);

  function processMemberships(groupMemberships: Array<GroupMembership>) {
    const directMessages: Array<GroupMembership> = [];
    const gMemberships: Array<GroupMembership> = [];
    const meetings: Array<GroupMembership> = [];

    for (const member of groupMemberships) {
      console.log("membership:", member);
      if (member.grouptype === GroupType.dm) {
        directMessages.push(member);
      } else if (member.grouptype === GroupType.meeting) {
        meetings.push(member);
      } else {
        gMemberships.push(member);
      }
    }
    setGroupMembers(gMemberships);
    setDMs(directMessages);
    setMeetings(meetings);
  }

  async function removeMember(index: number, groupMembership: GroupMembership) {
    if (!authContext.membership) { return; }
    if (groupMembership.grouptype === GroupType.dm) { return; }
    try {
      await groupServiceFactory().deleteGroupMemberForUser(
        authContext.membership.accountID,
        groupMembership.groupID,
        groupMembership.userID);
 
      /*if (groupMembership.grouptype === GroupType.dm) {        
        const newArr = Array.from(directMessages);
        newArr.splice(index, 1);
        setDMs(newArr);
      } else*/ if (groupMembership.grouptype === GroupType.meeting) {
        const newArr = Array.from(meetings);
        newArr.splice(index, 1);
        setMeetings(newArr);
      } else {
        const newArr = Array.from(groupMemberships);
        newArr.splice(index, 1);
        setGroupMembers(newArr);
      }
    } catch (err) {
      props.onError(err as Error);
    }
  }

  async function removeDeviceFromGroup(groupMembership: GroupMembership, deviceMember: DeviceMembership) {
    if (!authContext.membership) { return; }
    try {
      await groupServiceFactory().deleteGroupMemberForUser(
        authContext.membership.accountID,
        groupMembership.groupID,
        groupMembership.userID,
        deviceMember._id);

      const i = groupMembership.devices.findIndex(d => d._id == deviceMember._id);
      if (i < 0) { return; }
      groupMembership.devices.splice(i, 1);

      if (groupMembership.grouptype === GroupType.dm) {        
        setDMs(Array.from(directMessages));
      } else if (groupMembership.grouptype === GroupType.meeting) {
        setMeetings(Array.from(meetings));
      } else {
        setGroupMembers(Array.from(groupMemberships));
      }
      
    } catch (err) {
      props.onError(err as Error);
    }
  }

  function doOpenMemberTypeMenu(event: React.MouseEvent<HTMLButtonElement>, groupMember: GroupMembership) {
    event.stopPropagation();
    event.preventDefault();
    if (memberTypeMenuAnchorEl) {
      setMemberTypeMenuAnchorEl(null);
      setMenuGroupMember(null);
    } else {
      setMemberTypeMenuAnchorEl(event.currentTarget);
      setMenuGroupMember(groupMember);
    }
  }

  async function setMemberType(memberType: GroupMemberType) {
    if (!menuGroupMember) { return; }
    try {
      setCurrentMemberType(memberType);

      await groupServiceFactory().updateGroupMembershipType(
        props.member.accountID, menuGroupMember.groupID, menuGroupMember.userID,
        menuGroupMember.groupname, memberType);
      menuGroupMember.groupMemberType = memberType;

      setMemberTypeMenuAnchorEl(null);
      setMenuGroupMember(null);

    } catch (err) {
      console.error("Error saving member type:", err);
      props.onError(err as Error);
    }
  }

  const renderMembership = (g: GroupMembership, index: number) =>
    <GroupMembershipComponent key={g.groupID} groupMember={g} devMap={devMap} index={index}
      onRemoveMember={removeMember}
      onRemoveDevice={removeDeviceFromGroup}
      onMemberTypeMenu={doOpenMemberTypeMenu} />;

  return <>
    {groupMemberships.length > 0 && <>
      <section className="group-member-list-container">
        <header className="group-member-section-header"><h2>Groups</h2></header>
        {groupMemberships.map(renderMembership) }
      </section>
    </>}
    {meetings.length > 0 && <>
      <section className="group-member-list-container">
        <header className="group-member-section-header"><h2>Meetings</h2></header>
        {meetings.map(renderMembership) }
      </section>
    </>}
    {directMessages.length > 0 && <>
      <section className="group-member-list-container">
        <header className="group-member-section-header"><h2>Direct Messages</h2></header>
        {directMessages.map(renderMembership) }
      </section>
    </>}
    <MemberTypeDropdown
      anchor={memberTypeMenuAnchorEl}
      currentMemberType={currentMemberType}
      currentSubmissionType={menuGroupMember?.submissiontype}
      setMemberType={setMemberType}
      onClose={() => { setMemberTypeMenuAnchorEl(null); setMenuGroupMember(null); }} />
  </>;
}

interface GroupMemberProps {
  groupMember: GroupMembership,
  devMap: {[id: string]: Device},
  index: number,
  onRemoveMember: (index: number, groupMembership: GroupMembership) => void,
  onRemoveDevice: (groupMembership: GroupMembership, deviceMember: DeviceMembership) => void,
  onMemberTypeMenu: (event: React.MouseEvent<HTMLButtonElement>, groupMember: GroupMembership) => void
}
function GroupMembershipComponent(props: GroupMemberProps): React.ReactElement<any, any> {
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [msgCounts, setMsgCounts] = React.useState<{[devID: string]: number} | null>(null);

  async function onChangeExpansion(_evt: React.SyntheticEvent<Element, Event>, expanded: boolean) {
    setExpanded(expanded);
    if (expanded && !msgCounts) {
      try {
        const accountID = props.groupMember.accountID;
        const groupID = props.groupMember.groupID;
        const userID = props.groupMember.userID;
        const results = await groupServiceFactory().getMessageCounts(accountID, groupID, userID);
        const countMap: {[devID: string]: number} = {};
        for (const r of results) {
          countMap[r._id] = r.count;
        }
        setMsgCounts(countMap);
      } catch (err) {
        console.error("Error fetching message counts:", err);
      }
    }
  }

  function renderDevice(devSel: DeviceMembership): React.ReactElement<any, any> {
    const device = props.devMap[devSel._id];
    const msgCount = (msgCounts ? msgCounts[devSel._id] : 0) || 0;
    const displayString = device?.displayString || devSel._id;
    return <div key={devSel._id} className="group-member-device-container flex">
      <div>
        <h5 className="device-name">{displayString}</h5>
        {device && device.modified && <div className="desc">Last Seen: {device.modified.format("MMM D, YYYY h:mma")}</div>}
        {msgCount > 0 && <div className="desc">Unread Messages: {msgCount}</div>}
      </div>
      <div className="right-controls">
        <Button onClick={() => props.onRemoveDevice(props.groupMember, devSel)}>Remove</Button>
      </div>
    </div>; 
  }

  return <Accordion disableGutters elevation={0} square={true}
      expanded={expanded}
      onChange={onChangeExpansion}
      className="group-member-accordion">
    <AccordionSummary data-user-id={props.groupMember.userID}
        expandIcon={<ExpandMoreIcon />}>
      <div className="group-member-header">
        <h4>{props.groupMember.groupname || <em>Empty Name</em>}</h4>
        <div className="right-controls">
          {props.groupMember.grouptype === GroupType.dm ?
            <span className="group-member-type">{props.groupMember.groupMemberType}</span> :
            <Button
                disableRipple disableTouchRipple
                onClick={e => props.onMemberTypeMenu(e, props.groupMember)}>
              {props.groupMember.groupMemberType}
            </Button>
            }
          { expanded && props.groupMember.grouptype !== GroupType.dm &&
            <Button onClick={() => props.onRemoveMember(props.index, props.groupMember)}>Remove All</Button>}
        </div>
      </div>
    </AccordionSummary>
    <AccordionDetails className="account-group-devices">
      {props.groupMember.devices.length <= 0 ?
        <div className="no-devices-container">
          <h5>No Devices</h5>
        </div> :

        props.groupMember.devices.map(renderDevice)
      }
    </AccordionDetails>
  </Accordion>;
}
